import { SETTINGS_BASE } from "@/features/settings/paths"
import { ACTION_NAMES, ActionName } from "../constants"
import type { ActionContext, NotificationDTO } from "../types"

const asString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null

/**
 * The one shared action-name → route map so renderers never hand-roll paths.
 * Routes verified against App.tsx:
 * - `/questionnaire/:questionnaireId/answers` (canonical entry; status-aware
 *   pages auto-redirect via useQuestionnaireStatusNavigation)
 * - `?question=` is the deep-link param read by useScrollToAnswerByQuery
 * - `/knowledgebase`, the settings Trial & usage page
 */
export const ACTION_ROUTES: Record<
  ActionName,
  (params: Record<string, unknown>) => string | null
> = {
  [ACTION_NAMES.OPEN_QUESTIONNAIRE]: (params) => {
    const questionnaireId = asString(params.questionnaireId)
    return questionnaireId ? `/questionnaire/${questionnaireId}/answers` : null
  },
  [ACTION_NAMES.OPEN_QUESTION]: (params) => {
    const questionnaireId = asString(params.questionnaireId)
    const responsePairId = asString(params.responsePairId)
    return questionnaireId && responsePairId
      ? `/questionnaire/${questionnaireId}/answers?question=${responsePairId}`
      : null
  },
  [ACTION_NAMES.OPEN_KNOWLEDGEBASE]: () => "/knowledgebase",
  // Settings, not its Trial & usage page: that page is only there for users on
  // a trial, and paid plans would land on "feature not available".
  [ACTION_NAMES.OPEN_USAGE]: () => SETTINGS_BASE,
  [ACTION_NAMES.OPEN_DECLARATION_REVIEW]: (params) => {
    const declarationId = asString(params.declarationId)
    return declarationId
      ? `/material-compliance/supplier-studio/declarations/${declarationId}/review`
      : null
  },
  [ACTION_NAMES.OPEN_DECLARATIONS]: (params) => {
    const base = "/material-compliance/supplier-studio/declarations"
    const declarationId = asString(params.declarationId)
    // Carry the id as ?retry= so the declarations list re-triggers processing
    // on arrival ("Try again" actually reprocesses, not just navigates).
    return declarationId ? `${base}?retry=${declarationId}` : base
  },
}

const isKnownActionName = (name: string): name is ActionName =>
  Object.prototype.hasOwnProperty.call(ACTION_ROUTES, name)

/** Unknown action names (or missing params) resolve to null = mark read only. */
export const resolveActionRoute = (
  action: NotificationDTO["action"],
): string | null => {
  if (!action || !isKnownActionName(action.name)) {
    return null
  }
  return ACTION_ROUTES[action.name](action.params ?? {})
}

/**
 * Default `onAction` used by every v1 renderer. Invoked from the page CTA button
 * and the popover row click, both of which have already called markActed (and the
 * backend's /{id}/acted sets read_at). So: navigate when the action resolves to a
 * route; only mark read when there is no route to go to — avoids a redundant
 * /{id}/read request on every CTA click. Never navigates blind.
 */
export const navigateForAction = (
  action: NotificationDTO["action"],
  ctx: ActionContext,
): void => {
  const route = resolveActionRoute(action)
  if (route) {
    ctx.closePopover()
    ctx.navigate(route)
    return
  }
  ctx.markRead()
}
